import { useTopicTaggingOrchestrator } from '@src/hooks/useTopicTaggingOrchestrator';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { type ClipTheme, type TopicParagraphMapping } from '@src/utility/story';
import {
  computeTopicsHash,
  computeTranscriptHash,
} from '@src/utility/topicTaggingHash';
import { runInAction } from 'mobx';
import { useEffect, useMemo, useState } from 'react';

import type { Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('hook:useTopicParagraphMapping');

export type TopicMappingStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseTopicParagraphMappingResult {
  status: TopicMappingStatus;
  mapping: TopicParagraphMapping | null;
}

export interface UseTopicParagraphMappingInput {
  enabled: boolean;
  story: Story | null;
  topics: ClipTheme[];
  originalElements: TranscriptElement[] | undefined;
  isTranscriptionReady: boolean;
  userIdentity?: { email?: string; id?: string };
}

/**
 * One-time topic tagging for the Transcript & Speakers tab. Thin React shell
 * over `TopicMappingOrchestrator`: owns status state and AbortController
 * lifecycle only. All caching, payload building, AI calls, and persistence
 * live in the orchestrator (see TARGET-ARCHITECTURE.md §"Forbidden arrows":
 * components -> repositories).
 */
export function useTopicParagraphMapping(
  input: UseTopicParagraphMappingInput,
): UseTopicParagraphMappingResult {
  const orchestrator = useTopicTaggingOrchestrator();

  const [state, setState] = useState<UseTopicParagraphMappingResult>({
    status: 'idle',
    mapping: null,
  });

  const {
    enabled,
    story,
    topics,
    originalElements,
    isTranscriptionReady,
    userIdentity,
  } = input;
  const storyId = story?.id ?? null;

  // A single derived key drives the effect. When it's null we stay idle; when
  // it changes we (re)run the orchestrator. Uses the same structural hashes
  // the orchestrator uses internally so the effect re-fires exactly when the
  // persisted cache would invalidate (paragraph split/merge, first/last word
  // edits, topic-set changes).
  const runKey = useMemo(() => {
    if (!enabled || !isTranscriptionReady) return null;
    if (!storyId || !story) return null;
    if (!topics.length || !originalElements?.length) return null;
    const topicsHash = computeTopicsHash(topics.map(t => t.title));
    const transcriptHash = computeTranscriptHash(originalElements);
    return `${storyId}:${topicsHash}:${transcriptHash}`;
  }, [enabled, isTranscriptionReady, storyId, story, topics, originalElements]);

  useEffect(() => {
    if (!runKey || !story || !storyId || !originalElements) {
      setState({ status: 'idle', mapping: null });
      return;
    }

    const abort = new AbortController();
    setState({ status: 'loading', mapping: null });

    orchestrator
      .getOrCompute({
        story,
        storyId,
        topics,
        originalElements,
        userIdentity,
        signal: abort.signal,
        applyMapping: mapping => {
          // Keep the in-memory story in sync so a same-session remount
          // hits the orchestrator's cache instead of re-calling the AI.
          // Wrapped in runInAction in case `story` is part of a MobX tree
          // — the orchestrator stays MobX-free by design.
          runInAction(() => {
            if (story.aiResponse) {
              story.aiResponse = {
                ...story.aiResponse,
                topicParagraphMapping: mapping,
              };
            } else {
              story.aiResponse = {
                responses: [],
                topicParagraphMapping: mapping,
              };
            }
          });
        },
      })
      .then(result => {
        if (abort.signal.aborted) return;
        if (!result.ok) {
          logger.error(
            'Topic tagging orchestrator returned error',
            result.error,
          );
          setState({ status: 'error', mapping: null });
          return;
        }
        if (result.value === null) {
          // Orchestrator's "no work to do" signal — preserve original
          // hook behavior of surfacing this as idle rather than ready.
          setState({ status: 'idle', mapping: null });
          return;
        }
        setState({ status: 'ready', mapping: result.value });
      })
      .catch(e => {
        if (abort.signal.aborted) return;
        logger.error('Topic tagging orchestrator threw', extractErrorInfo(e));
        setState({ status: 'error', mapping: null });
      });

    return () => abort.abort();
    // `runKey` collapses topics/originalElements/story-identity changes into a
    // single trigger; orchestrator/userIdentity are stable across the session.
  }, [runKey, orchestrator]);

  return state;
}
